import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Register() {
    const { t } = useTranslation();
    const { register } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await register(name, email, password);
            navigate("/account");
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] sm:min-h-[calc(100vh-5rem)] grid grid-cols-1 lg:grid-cols-2" data-testid="register-page">
            <div className="flex items-center justify-center p-5 sm:p-8 lg:p-16 py-10 sm:py-12 order-2 lg:order-1">
                <form onSubmit={submit} className="w-full max-w-md">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("auth.registerEyebrow")}</div>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tighter mb-8 sm:mb-10">
                        {t("auth.registerTitle")}
                    </h2>

                    <div className="space-y-5">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{t("auth.nameLabel")}</label>
                            <input
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                data-testid="register-name"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{t("auth.emailLabel")}</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                data-testid="register-email"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{t("auth.passwordMin")}</label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                data-testid="register-password"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="mt-5 border border-red-500/40 bg-red-500/10 text-red-700 text-xs p-3" data-testid="register-error">{error}</div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="mt-8 w-full bg-[#0089c8] hover:bg-[#0077b6] rounded-lg disabled:opacity-50 text-white font-bold uppercase tracking-[0.18em] text-xs py-4 transition-colors"
                        data-testid="register-submit"
                    >
                        {loading ? t("auth.submittingRegister") : t("auth.submitRegister")}
                    </button>

                    <div className="mt-6 text-xs text-neutral-500 leading-relaxed">
                        {t("auth.registerConfirm")}
                    </div>

                    <div className="mt-6 text-sm text-neutral-500 text-center">
                        {t("auth.alreadyRegistered")}{" "}
                        <Link to="/login" className="text-[#0077b6] hover:underline" data-testid="link-to-login">
                            {t("auth.signInLink")}
                        </Link>
                    </div>
                </form>
            </div>

            <div className="hidden lg:block relative bg-white overflow-hidden order-1 lg:order-2">
                <div className="absolute inset-0  opacity-40" />
                <div className="absolute inset-0 flex items-center justify-center p-16">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("auth.memberBenefitsEyebrow")}</div>
                        <h1 className="font-display text-5xl xl:text-6xl font-extrabold uppercase tracking-tighter leading-[0.95]">
                            {t("auth.memberBenefitsLine1")}
                            <br />
                            <span className="text-[#0077b6]">{t("auth.memberBenefitsLine2")}</span>.
                        </h1>
                        <ul className="mt-8 space-y-3 text-neutral-600 text-sm">
                            {(t("auth.memberBenefits", { returnObjects: true }) || []).map((b) => (
                                <li key={b}>{b}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
